// Board sharing: visibility, per-person access (board_access), and publishing to web (spec §17.1).
import type { BoardAccessEntry, BoardInfo, BoardRole, MembersResponse, PublishInfo, WorkspaceInfo } from '../../shared/api.ts';
import { del, get, patch, post, put } from '../api.ts';
import { h } from '../util/dom.ts';
import { formError, ROLE_LABELS, select } from './page.ts';

const BOARD_ROLES: BoardRole[] = ['editor', 'viewer'];

export function openShareDialog(board: BoardInfo, ws: WorkspaceInfo, onBoardChange: (b: BoardInfo) => void): void {
  const error = formError();
  const content = h('div', { class: 'stack' }, [h('p', { class: 'muted' }, ['Loading…'])]);
  const close = h('button', { type: 'button', class: 'btn' }, ['Done']);
  const dialog = h('dialog', { class: 'dialog', 'aria-labelledby': 'share-title' }, [
    h('h2', { id: 'share-title' }, [`Share “${board.title}”`]), error, content, h('div', { class: 'dialog-actions' }, [close]),
  ]);
  close.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => dialog.remove());
  document.body.append(dialog);
  dialog.showModal();

  const act = (work: () => Promise<unknown>) => {
    error.clear();
    return work().then(render, (err: unknown) => error.show(err));
  };

  /** Publishing changes the board (its link): keep the page's copy in step. */
  const publishing = (work: () => Promise<BoardInfo>) => act(async () => {
    board = await work();
    onBoardChange(board);
  });

  async function render(): Promise<void> {
    const published = await get<PublishInfo>(`/api/boards/${board.id}/publish`);
    // Someone who can only take a board down (its workspace went read-only) just gets that.
    if (!board.canManage) {
      content.replaceChildren(publishSection(published));
      return;
    }
    const [members, access] = await Promise.all([
      get<MembersResponse>(`/api/workspaces/${ws.id}/members`),
      get<BoardAccessEntry[]>(`/api/boards/${board.id}/access`),
    ]);
    const byId = new Map(members.members.map((m) => [m.user.id, m]));
    const granted = new Map(access.map((a) => [a.userId, a.role]));

    const visibility = select([
      ['workspace', `Everyone in ${ws.name} (guests only if added below)`],
      ['private', 'Only people added below'],
    ], board.visibility, { 'aria-label': 'Who can open this board' });
    visibility.addEventListener('change', () => void act(async () => {
      board = await patch<BoardInfo>(`/api/boards/${board.id}`, { visibility: visibility.value });
      onBoardChange(board);
    }));

    const rows = access.map((a) => {
      const m = byId.get(a.userId);
      const role = select(BOARD_ROLES.map((r) => [r, ROLE_LABELS[r]!]), a.role, { 'aria-label': 'Access' });
      role.addEventListener('change', () => void act(() => put(`/api/boards/${board.id}/access/${a.userId}`, { role: role.value })));
      const remove = h('button', { type: 'button', class: 'btn small subtle' }, ['Remove']);
      remove.addEventListener('click', () => void act(() => del(`/api/boards/${board.id}/access/${a.userId}`)));
      return h('li', { class: 'share-row' }, [
        h('div', {}, [h('strong', {}, [m?.user.name ?? 'Former member']), h('div', { class: 'muted' }, [m ? `${m.user.email} · ${ROLE_LABELS[m.role]}` : ''])]),
        role, remove,
      ]);
    });

    const creator = byId.get(board.createdBy);
    if (creator) {
      rows.unshift(h('li', { class: 'share-row' }, [
        h('div', {}, [h('strong', {}, [creator.user.name]), h('div', { class: 'muted' }, [creator.user.email])]),
        h('span', { class: 'muted' }, ['Created this board']),
      ]));
    }

    const candidates = members.members.filter((m) => !granted.has(m.user.id) && m.user.id !== board.createdBy);
    const addForm: Node[] = [];
    if (candidates.length) {
      const who = select(candidates.map((m) => [m.user.id, `${m.user.name} (${ROLE_LABELS[m.role]})`]), candidates[0]!.user.id, { 'aria-label': 'Person' });
      const role = select(BOARD_ROLES.map((r) => [r, ROLE_LABELS[r]!]), 'editor', { 'aria-label': 'Access' });
      const add = h('button', { type: 'button', class: 'btn primary' }, ['Add']);
      add.addEventListener('click', () => void act(() => put(`/api/boards/${board.id}/access/${who.value}`, { role: role.value })));
      addForm.push(h('div', { class: 'share-add' }, [who, role, add]));
    }

    content.replaceChildren(
      h('label', { class: 'field' }, [h('span', { class: 'field-label' }, ['Who can open this board']), visibility]),
      h('h3', {}, ['People added to this board']),
      rows.length ? h('ul', { class: 'share-list', role: 'list' }, rows) : h('p', { class: 'muted' }, ['Nobody yet.']),
      ...addForm,
      h('p', { class: 'field-hint' }, ['To add someone from outside the workspace, invite them as a guest on the Members page first.']),
      publishSection(published),
    );
  }

  /** "Publish to web": a read-only, live link anyone can open without an account (spec §17.1). */
  function publishSection(info: PublishInfo): HTMLElement {
    const section = h('section', { class: 'publish' }, [h('h3', {}, ['Publish to web'])]);
    if (info.blocked) {
      section.append(h('p', { class: 'muted' }, ['Publishing has been turned off for this workspace.']));
      return section;
    }
    if (!info.token) {
      section.append(h('p', { class: 'muted' }, ['Anyone with the link can view this board, live as it changes, without an account. They can’t edit it or see who’s working on it.']));
      if (board.canPublish) {
        const publish = h('button', { type: 'button', class: 'btn' }, ['Publish']);
        publish.addEventListener('click', () => void publishing(() => put<BoardInfo>(`/api/boards/${board.id}/publish`, { indexable: false })));
        section.append(publish);
      }
      return section;
    }
    const url = `${location.origin}/p/${info.token}`;
    const link = h('input', { type: 'text', readonly: '', value: url, 'aria-label': 'Public link', class: 'publish-link' });
    link.addEventListener('focus', () => link.select());
    const copy = h('button', { type: 'button', class: 'btn' }, ['Copy']);
    copy.addEventListener('click', () => {
      void navigator.clipboard?.writeText(url).then(() => { copy.textContent = 'Copied'; }, () => link.select());
    });
    section.append(h('div', { class: 'share-add' }, [link, copy]));
    if (board.visibility === 'private') section.append(h('p', { class: 'field-hint' }, ['This board is private in the workspace, but anyone with this link can see it.']));
    if (board.canPublish) {
      const indexable = h('input', { type: 'checkbox' });
      indexable.checked = info.indexable;
      indexable.addEventListener('change', () => void publishing(() => put<BoardInfo>(`/api/boards/${board.id}/publish`, { indexable: indexable.checked })));
      section.append(h('label', { class: 'check' }, [indexable, ' Allow search engines to show this board']));
    }
    section.append(h('p', { class: 'muted' }, [`Viewed ${info.views30} ${info.views30 === 1 ? 'time' : 'times'} in the last 30 days.`]));
    const actions: Node[] = [];
    if (board.canPublish) {
      const reset = h('button', { type: 'button', class: 'btn small' }, ['Replace link']);
      reset.addEventListener('click', () => {
        if (confirm('Replace the link? The current one will stop working, and anyone viewing through it will be disconnected.')) {
          void publishing(() => post<BoardInfo>(`/api/boards/${board.id}/publish/reset`));
        }
      });
      actions.push(reset);
    }
    if (board.canUnpublish) {
      const off = h('button', { type: 'button', class: 'btn small subtle danger-text' }, ['Unpublish']);
      off.addEventListener('click', () => void publishing(() => del<BoardInfo>(`/api/boards/${board.id}/publish`)));
      actions.push(off);
    }
    if (actions.length) section.append(h('div', { class: 'publish-actions' }, actions));
    return section;
  }

  void render().catch((err: unknown) => error.show(err));
}
